import { useState, useEffect } from "react";
import { TrendingUp, TrendingDown, Briefcase, RefreshCw, ChevronRight } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const SIGNAL_STYLES = {
  STRONG_BUY: { bg: "bg-emerald-500/15", border: "border-emerald-500/40", text: "text-emerald-400", dot: "bg-emerald-400" },
  BUY: { bg: "bg-emerald-500/10", border: "border-emerald-500/25", text: "text-emerald-400", dot: "bg-emerald-500/70" },
  MIXED: { bg: "bg-slate-700/30", border: "border-slate-600/30", text: "text-slate-400", dot: "bg-slate-500" },
  QUIET: { bg: "bg-slate-700/20", border: "border-slate-600/20", text: "text-slate-500", dot: "bg-slate-600" },
  SELL: { bg: "bg-amber-500/10", border: "border-amber-500/25", text: "text-amber-400", dot: "bg-amber-500/70" },
  STRONG_SELL: { bg: "bg-red-500/15", border: "border-red-500/40", text: "text-red-400", dot: "bg-red-400" },
};

function fmtUSD(n) {
  if (!n) return "$0";
  if (Math.abs(n) >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

export default function InsiderSmartMoney({ onTickerClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedTicker, setExpandedTicker] = useState(null);
  const [days, setDays] = useState(90);

  const fetchData = async (lookback = days) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/insiders/smart-money`, {
        params: { days: lookback },
        timeout: 60000,
      });
      setData(r.data);
    } catch (e) {
      console.error("Smart money fetch error", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (!data && !loading) {
    return (
      <div className="glass-card rounded-xl p-5 border border-slate-700" data-testid="smart-money-panel">
        <button onClick={() => fetchData()} className="text-slate-300 hover:text-white text-sm" data-testid="smart-money-load-btn">
          Charger Smart Money…
        </button>
      </div>
    );
  }

  return (
    <div className="glass-card rounded-xl border border-cyan-500/20 overflow-hidden" data-testid="smart-money-panel">
      <div className="bg-gradient-to-r from-cyan-500/10 via-blue-500/10 to-transparent px-5 py-4 border-b border-slate-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Briefcase className="w-5 h-5 text-cyan-400" />
          <div>
            <h3 className="font-bold text-white text-base">Smart Money — Insiders</h3>
            <p className="text-xs text-slate-400">Achats/ventes des dirigeants Mag7 + Banques (Form 4 SEC)</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select value={days} onChange={(e) => { const v = parseInt(e.target.value); setDays(v); fetchData(v); }}
            className="bg-slate-800 border border-slate-600 text-slate-300 text-xs rounded-lg px-2 py-1.5"
            data-testid="smart-money-period-select">
            <option value={30}>30j</option>
            <option value={90}>90j</option>
            <option value={180}>6 mois</option>
            <option value={365}>1 an</option>
          </select>
          <button onClick={() => fetchData()} disabled={loading}
            className="text-slate-400 hover:text-white transition-colors disabled:opacity-40"
            data-testid="smart-money-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {data?.error && (
        <div className="p-5 text-center text-sm text-amber-400">⚠️ {data.error}</div>
      )}

      {data?.groups && (
        <div className="divide-y divide-slate-800">
          {data.groups.map((g) => (
            <div key={g.id} className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-base">{g.flag}</span>
                  <span className="font-bold text-white text-sm">{g.label}</span>
                  {g.summary.strong_buy_count > 0 && (
                    <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-[10px] font-bold rounded">
                      {g.summary.strong_buy_count} STRONG BUY
                    </span>
                  )}
                  {g.summary.strong_sell_count > 0 && (
                    <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-[10px] font-bold rounded">
                      {g.summary.strong_sell_count} STRONG SELL
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-slate-500 font-mono">
                  Net: <span className={g.summary.total_buy_value > g.summary.total_sell_value ? "text-emerald-400" : "text-red-400"}>
                    {fmtUSD(g.summary.total_buy_value - g.summary.total_sell_value)}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {g.tickers.map((t) => {
                  const s = SIGNAL_STYLES[t.signal] || SIGNAL_STYLES.MIXED;
                  const isExpanded = expandedTicker === `${g.id}-${t.ticker}`;
                  return (
                    <div key={t.ticker} className={`rounded-lg border ${s.border} ${s.bg} overflow-hidden`}
                      data-testid={`smart-money-${t.ticker}`}>
                      <button onClick={() => setExpandedTicker(isExpanded ? null : `${g.id}-${t.ticker}`)}
                        className="w-full px-3 py-2.5 text-left hover:brightness-125 transition-all">
                        <div className="flex items-center justify-between mb-1">
                          <button onClick={(e) => { e.stopPropagation(); onTickerClick && onTickerClick(t.ticker); }}
                            className="font-mono font-bold text-white text-sm hover:text-cyan-300">
                            {t.ticker}
                          </button>
                          <span className={`w-2 h-2 rounded-full ${s.dot}`}></span>
                        </div>
                        <div className={`text-[10px] font-bold ${s.text} mb-0.5 truncate`}>{t.signal_label}</div>
                        <div className="flex items-center gap-2 text-[10px] font-mono">
                          {t.buy_value_usd > 0 && (
                            <span className="text-emerald-400 flex items-center gap-0.5">
                              <TrendingUp className="w-2.5 h-2.5" />{fmtUSD(t.buy_value_usd)}
                            </span>
                          )}
                          {t.sell_value_usd > 0 && (
                            <span className="text-red-400 flex items-center gap-0.5">
                              <TrendingDown className="w-2.5 h-2.5" />{fmtUSD(t.sell_value_usd)}
                            </span>
                          )}
                        </div>
                      </button>

                      {isExpanded && t.top_txns?.length > 0 && (
                        <div className="border-t border-slate-700/50 bg-slate-900/40 px-3 py-2 space-y-1.5">
                          {t.top_txns.map((tx, i) => (
                            <div key={i} className="flex items-center justify-between text-[10px]">
                              <div className="flex items-center gap-1 min-w-0">
                                <span className={`w-1.5 h-1.5 rounded-full ${tx.signal === "BUY" ? "bg-emerald-400" : "bg-red-400"} shrink-0`}></span>
                                <span className="text-slate-300 truncate" title={tx.name}>{tx.name.split(" ")[0]}</span>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <span className="text-slate-500">{tx.txn_date.slice(5)}</span>
                                <span className={tx.signal === "BUY" ? "text-emerald-400 font-bold" : "text-red-400 font-bold"}>
                                  {fmtUSD(tx.txn_value_usd)}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {data?.fetched_at && (
        <div className="px-5 py-2 bg-slate-900/30 border-t border-slate-800 text-[10px] text-slate-500 text-right">
          MAJ: {new Date(data.fetched_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
        </div>
      )}
    </div>
  );
}
